"use client";

import { useCallback, useRef, type FC, type KeyboardEvent, type ChangeEvent } from "react";

// macOS-style segmented date input — three auto-advancing fields in ISO order
// (YYYY-MM-DD). Typing flows naturally: when a segment fills, focus advances
// to the next. Backspace on empty retreats to the previous segment.

export interface SegmentedDateInputProps {
  value: string; // "YYYY-MM-DD" or partial
  onChange: (iso: string) => void;
  onSubmit?: () => void;
  autoFocus?: boolean;
}

type Segment = { max: number; placeholder: string; width: number };

const SEGMENTS: Segment[] = [
  { max: 4, placeholder: "YYYY", width: 44 },
  { max: 2, placeholder: "MM", width: 28 },
  { max: 2, placeholder: "DD", width: 28 },
];

function splitValue(iso: string): [string, string, string] {
  const parts = iso.split("-");
  return [parts[0] ?? "", parts[1] ?? "", parts[2] ?? ""];
}

function joinParts(parts: [string, string, string]): string {
  return parts.join("-");
}

export const SegmentedDateInput: FC<SegmentedDateInputProps> = ({
  value,
  onChange,
  onSubmit,
  autoFocus = false,
}) => {
  const refs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ] as const;

  const parts = splitValue(value);

  const updatePart = useCallback(
    (index: number, newVal: string) => {
      const next: [string, string, string] = [...parts] as [string, string, string];
      next[index] = newVal;
      onChange(joinParts(next));
    },
    [parts, onChange],
  );

  const handleChange = useCallback(
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, "");
      const seg = SEGMENTS[index]!;
      const clamped = raw.slice(0, seg.max);
      updatePart(index, clamped);

      // Auto-advance when segment is full
      if (clamped.length >= seg.max && index < 2) {
        refs[index + 1]?.current?.focus();
        refs[index + 1]?.current?.select();
      }
    },
    [updatePart, refs],
  );

  const handleKeyDown = useCallback(
    (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget;

      if (e.key === "Backspace" && input.value === "" && index > 0) {
        e.preventDefault();
        refs[index - 1]?.current?.focus();
        // Place cursor at end of previous segment
        const prev = refs[index - 1]?.current;
        if (prev) {
          const len = prev.value.length;
          prev.setSelectionRange(len, len);
        }
      }

      if (e.key === "ArrowLeft" && input.selectionStart === 0 && index > 0) {
        e.preventDefault();
        refs[index - 1]?.current?.focus();
      }

      if (e.key === "ArrowRight" && input.selectionStart === input.value.length && index < 2) {
        e.preventDefault();
        refs[index + 1]?.current?.focus();
        refs[index + 1]?.current?.setSelectionRange(0, 0);
      }

      if (e.key === "Enter") {
        e.preventDefault();
        onSubmit?.();
      }
    },
    [refs, onSubmit],
  );

  return (
    <span
      className="inline-flex items-center rounded-md border border-border bg-background px-2 py-1.5 focus-within:border-foreground/40 transition-colors"
      style={{ gap: 2 }}
    >
      {SEGMENTS.map((seg, i) => (
        <span key={i} className="inline-flex items-center" style={{ gap: 2 }}>
          {i > 0 && (
            <span className="text-sm text-muted-foreground select-none" style={{ lineHeight: 1 }}>
              -
            </span>
          )}
          <input
            ref={refs[i]}
            type="text"
            inputMode="numeric"
            value={parts[i]}
            onChange={handleChange(i)}
            onKeyDown={handleKeyDown(i)}
            onFocus={(e) => e.target.select()}
            placeholder={seg.placeholder}
            maxLength={seg.max}
            autoComplete="off"
            spellCheck={false}
            autoFocus={autoFocus && i === 0}
            className="text-sm tabular-nums text-center outline-none bg-transparent placeholder:text-muted-foreground/50"
            style={{ width: seg.width }}
          />
        </span>
      ))}
    </span>
  );
};
